import ExpandMore from "@mui/icons-material/ExpandMore";
import { Button, Menu, MenuItem, Stack, Typography } from "@mui/material";
import { NotificationDigestCadence } from "generated-graphql/graphql";
import { useState, type MouseEvent } from "react";

/**
 * How often email arrives, asked once for the whole person.
 *
 * Deliberately **not** a control on every row. "Which notifications email me" is
 * a question about each alert and belongs on that alert's line; "how often mail
 * arrives" is one answer, and asking it twenty times over would put twenty
 * dropdowns on this page to express a single preference — with nothing to say
 * when the answers disagreed.
 *
 * There is no "Off" option either, because the switch beside this already is one.
 * Two controls that can both silence email is two controls that can contradict
 * each other, and the reader would have no way to tell which one was winning.
 *
 * A text button rather than an outlined `Select`: it sits in a row of switches,
 * and a bordered field would out-weigh every control around it while adding
 * nothing — the value is visible either way.
 */

/**
 * What each cadence is called, and what it actually promises.
 *
 * "Immediate" is the reader's word and the honest description is beside it:
 * digested mail is composed by a job that runs hourly, so the truthful claim is
 * "as soon as we can" rather than "the instant it happens". Saying so here costs
 * one line and avoids a support ticket about a fifty-minute delay.
 */
const CADENCE_OPTIONS: ReadonlyArray<{
  value: NotificationDigestCadence;
  label: string;
  description: string;
}> = [
  {
    value: NotificationDigestCadence.Immediate,
    label: "Immediately",
    description: "As soon as we can, usually within the hour",
  },
  { value: NotificationDigestCadence.Daily, label: "Daily", description: "One email each morning" },
  { value: NotificationDigestCadence.Weekly, label: "Weekly", description: "Monday mornings" },
  {
    value: NotificationDigestCadence.Monthly,
    label: "Monthly",
    description: "The first of the month",
  },
];

function cadenceLabel(cadence: NotificationDigestCadence): string {
  return CADENCE_OPTIONS.find((option) => option.value === cadence)?.label ?? "Daily";
}

/**
 * The send hour as a reader would say it. Whole hours only, because the platform
 * only ever sends on the hour — "8:00 AM" would imply a precision the schedule
 * does not have, and "08" would read like a form field.
 */
function formatSendHour(hourLocal: number): string {
  const suffix = hourLocal < 12 ? "am" : "pm";
  const twelve = hourLocal % 12 === 0 ? 12 : hourLocal % 12;
  return `${twelve}${suffix}`;
}

export type DigestCadenceControlProps = {
  cadence: NotificationDigestCadence;
  /** Local hour digests go out at, for the sentence around the control. */
  sendHourLocal: number;
  /** True while a write is in flight, matching the switches beside it. */
  disabled: boolean;
  onChange: (cadence: NotificationDigestCadence) => void;
};

export function DigestCadenceControl({
  cadence,
  sendHourLocal,
  disabled,
  onChange,
}: DigestCadenceControlProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const openMenu = (event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const closeMenu = () => setAnchorEl(null);
  const choose = (value: NotificationDigestCadence) => {
    closeMenu();
    if (value !== cadence) {
      onChange(value);
    }
  };

  // The trailing clause only applies to the scheduled cadences. "Immediately …
  // around 8am" is a contradiction, and a reader who picked immediate has no send
  // hour to be told about.
  const scheduled = cadence !== NotificationDigestCadence.Immediate;

  return (
    <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
      {/*
        "Email summaries", never "Email". Cadence governs only the alerts the
        product gathers up — an alert it sends the moment it happens still does,
        whatever is chosen here — so a sentence starting "Email arrives Weekly"
        would promise something this control cannot deliver, and would read as
        broken the first time an immediate alert landed on a Tuesday.
      */}
      <Typography variant="body2" color="text.secondary">
        Email summaries arrive
      </Typography>
      <Button
        size="small"
        onClick={openMenu}
        disabled={disabled}
        endIcon={<ExpandMore />}
        // Tight to the words on either side so the sentence reads as a sentence
        // rather than as a label, a control, and a second label.
        sx={{ minWidth: 0, px: 0.75, py: 0, textTransform: "none", fontWeight: 600 }}
        aria-haspopup="menu"
        aria-expanded={anchorEl !== null}
      >
        {cadenceLabel(cadence)}
      </Button>
      {scheduled && (
        <Typography variant="body2" color="text.secondary">
          around {formatSendHour(sendHourLocal)}, your local time.
        </Typography>
      )}

      <Menu
        anchorEl={anchorEl}
        open={anchorEl !== null}
        onClose={closeMenu}
        // Named because the button's own text is the current value, which tells a
        // screen-reader user what it says and not what choosing does.
        MenuListProps={{ "aria-label": "How often email arrives" }}
      >
        {CADENCE_OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            selected={option.value === cadence}
            onClick={() => choose(option.value)}
          >
            <Stack>
              <Typography variant="body2">{option.label}</Typography>
              <Typography variant="caption" color="text.secondary">
                {option.description}
              </Typography>
            </Stack>
          </MenuItem>
        ))}
      </Menu>
    </Stack>
  );
}
